import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { Form, ButtonsBlock, Button, UtilsBlock, Spinner } from '@hz-design-system/web-ui';
import trackEvent from '../../../utils/trackEvent';
import { stepLabels, ACTION_ACCOUNT_CONNECT_CLICK } from '../../../constants/gaConstants';

import Classes from './AdmarktRegistrationStepLinkAccount.module.scss';
import { FormData } from '../types';
import { LoggedInUser } from '../../../../../../types/AppState';

type AdmarktRegistrationStepLinkAccountProps = {
  userEmail: string;
  store: FormData;
  onFinalSubmit: (data: FormData) => void;
  handleShowLoginClick: () => void;
  handleCreateAccountClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  loggedInUser: LoggedInUser;
};

const AdmarktRegistrationStepLinkAccount = ({
  userEmail,
  store,
  onFinalSubmit,
  handleShowLoginClick,
  handleCreateAccountClick,
  loggedInUser,
}: AdmarktRegistrationStepLinkAccountProps) => {
  const i18n = useI18nContext();
  const userHasEmail = Boolean(userEmail);
  const [isLoading, setIsLoading] = useState(false);

  const { handleSubmit, reset } = useForm({ mode: 'onTouched', defaultValues: store });

  useEffect(() => {
    reset(store);
  }, [store]);

  const onBeforeSubmit = (data: FormData) => {
    setIsLoading(true);
    if (loggedInUser) {
      onFinalSubmit(data);
    } else {
      trackEvent({ eventAction: ACTION_ACCOUNT_CONNECT_CLICK, eventLabel: stepLabels.STEP4 });
      handleShowLoginClick();
      setIsLoading(false);
    }
  };

  return (
    <section className={Classes.root}>
      <Form onSubmit={handleSubmit(onBeforeSubmit)}>
        {userHasEmail ? (
          <>
            <UtilsBlock textStyle="link-regular">
              <UtilsBlock pt="l">
                <Trans tagName="h3" i18nKey="admarktRegistration.step.link_account.list_title" />
              </UtilsBlock>
              <Trans
                tagName="span"
                i18nKey="admarktRegistration.step.link_account.loggedin_user_info"
                email={userEmail}
              />
            </UtilsBlock>
            <ul className={Classes.infoList}>
              <li>
                <Trans
                  tagName="strong"
                  i18nKey="admarktRegistration.step.link_account.continue_current_account.title"
                />
                <Trans tagName="p" i18nKey="admarktRegistration.step.link_account.continue_current_account.text" />
              </li>
            </ul>
            {isLoading ? (
              <Spinner />
            ) : (
              <ButtonsBlock kind="adaptive">
                <Button.Primary data-testid="link-this-account">
                  {i18n.t('admarktRegistration.step.button.link_this')}
                </Button.Primary>
              </ButtonsBlock>
            )}
          </>
        ) : (
          <>
            <UtilsBlock pt="l">
              <Trans tagName="h3" i18nKey="admarktRegistration.step.link_account.list_title_logged_out" />
            </UtilsBlock>
            <ul className={Classes.infoList}>
              <li>
                <Trans tagName="strong" i18nKey="admarktRegistration.step.link_account.existing_account.title" />
                <Trans tagName="p" i18nKey="admarktRegistration.step.link_account.existing_account.text" />
              </li>
              <li>
                <Trans tagName="strong" i18nKey="admarktRegistration.step.no_account.title" />
                <Trans tagName="p" i18nKey="admarktRegistration.step.no_account.text" />
              </li>
            </ul>
            <ButtonsBlock kind="adaptive">
              <Button.Secondary data-testid="create-account" onClick={handleCreateAccountClick}>
                {i18n.t('admarktRegistration.step.button.create_account')}
              </Button.Secondary>
              <Button.Primary data-testid="link-existing-account">
                {i18n.t('admarktRegistration.step.button.link_existing')}
              </Button.Primary>
            </ButtonsBlock>
          </>
        )}
      </Form>
    </section>
  );
};

export default AdmarktRegistrationStepLinkAccount;
